import { API_BASE, DEFAULT_HEADERS } from "./apiClient";
import { uploadFileWithPresign } from "./uploadService";

const authToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

// Create a feed post — encapsulates media upload + the POST request.
//
// NOTE: the backend's exact field shape isn't pinned down, so this still tries
// a few known-good payload shapes (legacy from Dashboard.js). This is now the
// SINGLE place that knows about that ambiguity; collapse to one shape once the
// API contract is confirmed. Callers just `await createPost(...)`.
export async function createPost({
  body,
  mediaFile,
  mediaType,
  userId,
  tags,
  scope,
  teamKey,
}) {
  const text = (body || "").trim();

  // Tags arrive as "scope:key" tokens (e.g. "region:Latin America", "theme:7").
  // Accept the legacy single scope/teamKey too. The first tag is the primary
  // scope (backward-compatible); the rest go in a `scopes` array for backends
  // that support multi-tagging — and degrade gracefully if they don't.
  const tokens =
    Array.isArray(tags) && tags.length
      ? tags
      : scope && teamKey
      ? [`${scope}:${teamKey}`]
      : [];
  const parsedTags = tokens
    .map((tok) => {
      const i = String(tok).indexOf(":");
      return i > 0
        ? { scope: String(tok).slice(0, i), team_key: String(tok).slice(i + 1) }
        : null;
    })
    .filter(Boolean);
  const primaryScope = parsedTags[0];
  const scopeFields = primaryScope
    ? { scope: primaryScope.scope, team_key: primaryScope.team_key }
    : {};
  const multiScope = parsedTags.length > 1 ? { scopes: parsedTags } : {};

  // 1) Upload media first (if any)
  let media_url = null;
  let media_type = null;
  if (mediaFile) {
    const uploadResult = await uploadFileWithPresign({
      kind: "post",
      file: mediaFile,
      userId: userId || undefined,
    });
    media_url = uploadResult.publicUrl;
    media_type =
      mediaType || (mediaFile.type?.startsWith("video") ? "video" : "image");
  }

  const headers = {
    "Content-Type": "application/json",
    ...DEFAULT_HEADERS,
    ...(authToken() ? { Authorization: `Bearer ${authToken()}` } : {}),
    ...(userId ? { "X-User-Id": String(userId) } : {}),
  };
  const userFields = userId ? { user: { id: userId }, user_id: userId } : {};

  const attempts = [
    // Explicit columns most backends expect — incl. multi-scope tags
    {
      body: text,
      ...(media_url && media_type === "image" ? { image_url: media_url } : {}),
      ...(media_url && media_type === "video" ? { video_url: media_url } : {}),
      ...userFields,
      ...scopeFields,
      ...multiScope,
    },
    // Same, but drop the multi-scope array (backend may reject unknown field)
    {
      body: text,
      ...(media_url ? { media_url } : {}),
      ...(media_type ? { media_type } : {}),
      ...userFields,
      ...scopeFields,
    },
    // Nested media object
    {
      body: text,
      ...(media_url
        ? { media: { url: media_url, type: media_type || undefined } }
        : {}),
      ...userFields,
    },
    // Minimal fallback (no media)
    { body: text, ...userFields },
  ];

  let created = null;
  let lastErrText = "";
  for (const payload of attempts) {
    const res = await fetch(`${API_BASE}/feed/posts`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      try {
        created = await res.json();
      } catch {
        created = { id: Date.now(), ...payload };
      }
      break;
    }
    try {
      lastErrText = await res.text();
    } catch {}
    // Only retry the next shape on a validation error
    if (res.status && res.status !== 400) break;
  }

  if (!created) {
    throw new Error(
      `Failed to create post.${lastErrText ? `\nServer says: ${lastErrText}` : ""}`
    );
  }

  return { created, media_url, media_type };
}

export async function deletePost(postId, currentUserId) {
  const res = await fetch(`${API_BASE}/feed/posts/${postId}`, {
    method: "DELETE",
    headers: {
      ...DEFAULT_HEADERS,
      "Content-Type": "application/json",
      ...(authToken() ? { Authorization: `Bearer ${authToken()}` } : {}),
      ...(currentUserId ? { "X-User-Id": String(currentUserId) } : {}),
    },
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Failed with ${res.status}: ${txt}`);
  }
}
